/**
 * Who is on a document, and what colour they are — the pure half of presence
 * (§8.3).
 *
 * No React and no Yjs in here on purpose: a colour that is stable is a claim
 * that can be tested, and a test that has to stand up a WebSocket to check it
 * would not be run.
 */

/** One person on the document, as the presence UI needs them. */
export interface PresenceFace {
  personId: string;
  name: string;
  color: string;
}

/**
 * Ten hues, spaced around the wheel, all dark enough to carry white text.
 *
 * The caret label and the avatar circle both paint white on this colour, so a
 * mid-tone that looks pleasant on a light page and fails on a near-black page is not a
 * candidate. These are Untitled UI 600/700-level tones for that reason.
 */
const PALETTE: readonly string[] = [
  '#d92d20', // red
  '#e04f16', // orange
  '#b54708', // amber
  '#079455', // green
  '#107569', // teal
  '#0086c9', // cyan
  '#1570ef', // blue
  '#444ce7', // indigo
  '#6938ef', // violet
  '#dd2590', // pink
];

/**
 * FNV-1a, 32-bit. Small, dependency-free, and — the only property that matters
 * here — identical on every machine and every reload.
 */
function fnv1a(value: string): number {
  let hash = 0x811c9dc5;
  for (let i = 0; i < value.length; i += 1) {
    hash ^= value.charCodeAt(i);
    hash = Math.imul(hash, 0x01000193);
  }
  return hash >>> 0;
}

/**
 * The colour a person is, everywhere, forever.
 *
 * Derived from the id rather than assigned on connect, and this is the whole
 * point: App Platform deploys drop WebSocket connections (§8.3) and Yjs
 * reconnects, which means a randomly-assigned colour changes mid-session. A
 * colleague whose caret was green and is now orange reads as a second person
 * having joined — presence that invents people is worse than no presence.
 *
 * Two people can collide on a colour. That is a cosmetic tie broken by the name
 * beside the caret, and it is the correct trade against re-deriving colours
 * from the current roster, which would change everyone's colour when someone
 * new opens the document.
 */
export function presenceColor(personId: string): string {
  return PALETTE[fnv1a(personId) % PALETTE.length] as string;
}

/** One or two letters for an avatar circle. Falls back to `?` for a blank name. */
export function initials(name: string): string {
  const words = name.trim().split(/\s+/).filter(Boolean);
  if (words.length === 0) return '?';
  const first = (words[0] as string)[0] ?? '';
  const last = words.length > 1 ? ((words[words.length - 1] as string)[0] ?? '') : '';
  return (first + last).toUpperCase();
}

/** What a client publishes about itself on the awareness channel. */
export interface AwarenessState {
  clientId: number;
  user?: { personId?: unknown; name?: unknown; color?: unknown };
}

export interface PresenceRoster {
  /** The signed-in person, if this client has published itself yet. */
  self: PresenceFace | null;
  /** Everyone else, in a stable order. */
  others: PresenceFace[];
}

/**
 * Turns the raw awareness states into faces.
 *
 * Deduplicated by `personId`, not by `clientId`: one person with the document
 * open in two tabs is two Yjs clients and one human, and a presence bar that
 * shows them twice is telling the reader something false about who is in the
 * room. The first state wins so the order does not flicker as tabs come and go.
 *
 * Ordering is by person id rather than by arrival, so the row does not reshuffle
 * every time someone's awareness beat lands.
 */
export function rosterFromAwareness(
  states: readonly AwarenessState[],
  selfClientId: number | null,
): PresenceRoster {
  const faces = states.map((state) => ({ clientId: state.clientId, face: faceFrom(state) }));

  // Self is resolved first, before anything is pushed into `others`. Doing it in
  // one pass would put a second tab of the signed-in person into `others` when
  // that tab's state happens to arrive first — the exact double-count the
  // deduplication below exists to prevent.
  const self =
    faces.find((entry) => selfClientId !== null && entry.clientId === selfClientId)?.face ?? null;

  const seen = new Set<string>(self ? [self.personId] : []);
  const others: PresenceFace[] = [];
  for (const entry of faces) {
    if (!entry.face || seen.has(entry.face.personId)) continue;
    seen.add(entry.face.personId);
    others.push(entry.face);
  }

  others.sort((a, b) => a.personId.localeCompare(b.personId));
  return { self, others };
}

/**
 * A state is a face only once it carries an identity; a bare clock is not one.
 *
 * Blank strings are refused as firmly as missing fields. A client that has
 * connected but not yet resolved who it is publishes an awareness state
 * regardless, and rendering that as a nameless avatar puts a ghost in the room
 * for every connection in flight.
 */
function faceFrom(state: AwarenessState): PresenceFace | null {
  const user = state.user;
  if (!user || typeof user.personId !== 'string' || typeof user.name !== 'string') return null;
  if (!user.personId.trim() || !user.name.trim()) return null;
  return {
    personId: user.personId,
    name: user.name,
    color: typeof user.color === 'string' ? user.color : presenceColor(user.personId),
  };
}
